import React from 'react';
import PropTypes from 'prop-types';
import get, { fromEntries } from 'src/utils/lodash-alternatives';
import { denormalize } from './layout';

const makeConnect = store => (mapStateToProps, mapCallbackToProps = {}) => {
  const getState = () => mapStateToProps(store.getState());
  const getCallbacks =
    typeof mapCallbackToProps === 'function'
      ? ownProps => mapCallbackToProps(store.dispatch, ownProps)
      : (() => {
          if (!Object.fromEntries) Object.fromEntries = fromEntries;
          const callbacks = Object.fromEntries(
            Object.entries(mapCallbackToProps).map(([name, callback]) => [
              name,
              (...args) => store.dispatch(callback(...args)),
            ])
          );
          return () => callbacks;
        })();
  return Component =>
    class extends React.PureComponent {
      static displayName = Component.displayName || Component.name;

      constructor(props) {
        super(props);
        this.state = getState();
        this.mounted = false;
      }

      componentDidMount() {
        this.mounted = true;
        this.unsub = store.subscribe(() => {
          if (this.mounted) {
            this.setState(getState());
          }
        });
      }

      componentWillUnmount() {
        this.mounted = false;
        this.unsub();
      }

      render() {
        return (
          <Component
            {...this.props}
            {...this.state}
            {...getCallbacks(this.props)}
          />
        );
      }
    };
};

const createConnectedInventory = (componentInventory, connect) => {
  const connectedInventory = new Map();
  return {
    has(type) {
      return componentInventory.has(type);
    },
    get(type, id = type) {
      if (!componentInventory.has(type)) {
        return undefined;
      }
      const { component, callbacks } = componentInventory.get(type);
      const compoundKey = `${type}-${id}`;
      if (!connectedInventory.has(compoundKey)) {
        const connector = connect(makeNodeSelector(id), callbacks);
        const connected = connector(component);
        connectedInventory.set(compoundKey, { component: connected });
      }
      return connectedInventory.get(compoundKey);
    },
  };
};

const createPresenter = (componentInventory, store) => {
  const connect = makeConnect(store);
  const inventory = createConnectedInventory(componentInventory, connect);
  const Presenter = React.memo(({ root, components }) =>
    !root
      ? null
      : denormalize(
          { root, components },
          ({ id, type, properties, children }) =>
            inventory.has(type) === false
              ? null
              : React.createElement(
                  inventory.get(type, id).component,
                  { ...properties, key: id },
                  children
                )
        )
  );
  Presenter.propTypes = {
    components: PropTypes.objectOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        properties: PropTypes.object.isRequired,
        children: PropTypes.arrayOf(PropTypes.string),
      })
    ).isRequired,
    root: PropTypes.string,
  };
  return connect(makeRootSelector())(Presenter);
};

const makeRootSelector = () => state => ({
  root: get(state, 'root'),
  components: get(state, 'components'),
});

const makeNodeSelector = type => {
  const emptyStateFragment = {};
  return state =>
    get(state, `componentsProperties.${type}`, emptyStateFragment);
};

export default createPresenter;
